<template>
    <div class="mx-auto max-w-screen-2xl grid sm:grid-cols-3 gap-12">
        <div class="col-span-2">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Recent Interactions</h3>
            <div class="bg-gray-50 shadow overflow-hidden sm:rounded-md border border-gray-200" v-if="activity">
                <ul role="list" class="divide-y divide-gray-300">
                    <li v-for="act in activity" :key="act.datetime">
                        <a href="#" class="block bg-white hover:bg-gray-100">
                            <div class="flex items-center px-4 py-4 sm:px-6">
                                <div class="min-w-0 flex-1 flex items-center">
                                    <div class="flex-shrink-0">
                                        <NewspaperIcon v-if="act.type === 'JournoLink\\Shared\\Models\\PressRelease'" class="h-8 w-8 text-blue-400" />
                                        <EyeIcon v-if="act.type === 'JournoLink\\Shared\\Models\\PressReleaseView'" class="h-8 w-8 text-indigo-400" />
                                        <EnvelopeIcon v-if="act.type === 'JournoLink\\Shared\\Models\\MediaRequest'" class="h-8 w-8 text-green-400" />
                                        <EnvelopeOpenIcon v-if="act.type === 'JournoLink\\Shared\\Models\\MediaRequestResponse'" class="h-8 w-8 text-purple-400" />
                                        <PencilSquareIcon v-if="act.type === 'JournoLink\\Shared\\Models\\ProfessionalWriting'" class="h-8 w-8 text-red-400" />
                                    </div>
                                    <div class="min-w-0 flex-1 px-4 md:grid md:grid-cols-2 md:gap-4">
                                        <div>
                                            <p class="text-md font-medium text-gray-800 truncate">
                                                <template v-if="act.type === 'JournoLink\\Shared\\Models\\PressRelease'">Published a Press Release</template>
                                                <template v-if="act.type === 'JournoLink\\Shared\\Models\\PressReleaseView'">Viewed a Press Release</template>
                                                <template v-if="act.type === 'JournoLink\\Shared\\Models\\MediaRequest'">Submitted a Media Request</template>
                                                <template v-if="act.type === 'JournoLink\\Shared\\Models\\MediaRequestResponse'">Responded to a Media Request</template>
                                                <template v-if="act.type === 'JournoLink\\Shared\\Models\\ProfessionalWriting'">Created a Professional Writing Request</template>
                                            </p>
                                            <p class="mt-2 flex items-center text-sm text-gray-500">
                                                <span class="truncate">{{ act.title }}</span>
                                            </p>
                                        </div>
                                        <div class="hidden md:block">
                                            <div>
                                                <p class="text-sm text-gray-900">
                                                    <time :datetime="act.datetime">{{ formatDateRelative(act.datetime) }}</time>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <ChevronRightIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <div class="col-span-1">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Mixpanel Feed</h3>
            <div class="flow-root" v-if="feed">
                <ul role="list" class="-mb-8 px-6">
                    <li v-for="(event, eventIdx) in feed" :key="event.properties.$insert_id">
                        <div class="relative pb-8">
                            <span v-if="eventIdx !== feed.length - 1" class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            <div class="relative flex space-x-3">
                                <div>
                                  <span class="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-blue-400">
                                      <DocumentIcon v-if="event.event.includes('Page Visit')" class="h-5 w-5 text-white" aria-hidden="true" />
                                      <CursorArrowRaysIcon v-else class="h-5 w-5 text-white" aria-hidden="true" />
                                  </span>
                                </div>
                                <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                    <div>
                                        <p class="text-sm text-gray-500">
                                            {{ event.event }}
                                        </p>
                                    </div>
                                    <div class="text-right text-sm whitespace-nowrap text-gray-500">
                                        {{ formatDateShort(event.properties.time * 1000) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            <div class="text-sm text-gray-400" v-if="loadingActivity">
                Loading recent activity from MixPanel
            </div>

            <div class="text-sm text-gray-400" v-if="!loadingActivity && feed.length === 0">
                MixPanel hasn't recorded any recent activity from this user
            </div>
        </div>
    </div>
</template>

<script>
import {ref, onMounted} from "vue"
import {CursorArrowRaysIcon, DocumentIcon} from "@heroicons/vue/24/solid"
import {useRouter} from "vue-router"
import {ChevronRightIcon, EyeIcon, EnvelopeIcon, EnvelopeOpenIcon, NewspaperIcon, PencilSquareIcon} from "@heroicons/vue/24/outline"

export default {
    name: "Activity",
    components: {
        DocumentIcon,
        CursorArrowRaysIcon,
        NewspaperIcon,
        EyeIcon,
        EnvelopeIcon,
        EnvelopeOpenIcon,
        PencilSquareIcon,
        ChevronRightIcon,
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const router = useRouter()
        const activity = ref([])
        const loadingActivity = ref(false)
        const feed = ref([])

        const getMixpanelActivity = async () => {
            loadingActivity.value = true
            await axios.get(route('users.activity.feed', { user: props.user.id }))
                .then((response) => {
                    feed.value = response.data.results.events.sort((a, b) => b.properties.time - a.properties.time)
                    loadingActivity.value = false
                })
                .catch((error) => {
                    loadingActivity.value = false
                })
        }

        const getActivity = async () => {
            await axios.get(route('users.activity', { user: props.user.id }))
                .then((response) => {
                    activity.value = response.data.activity
                })
        }

        const navigateToActivity = (activity) => {
            switch (activity.type) {
                case 'JournoLink\\Shared\\Models\\PressRelease':
                case 'JournoLink\\Shared\\Models\\PressReleaseView':
                    router.push({ name: 'releases.show', params: {releaseId: activity.id} })
                    break
                case 'JournoLink\\Shared\\Models\\MediaRequest':
                case 'JournoLink\\Shared\\Models\\MediaRequestResponse':
                    router.push({ name: 'mediarequests.show', params: {mediaRequestId: activity.id} })
                    break
                case 'JournoLink\\Shared\\Models\\ProfessionalWriting':
                    router.push({ name: 'professionalWriting.show', params: {professionalWritingId: activity.id} })
                    break
                default:
                    break
            }
        }

        onMounted(() => {
            window.location.hash = 'activity'
            getActivity()
            getMixpanelActivity()
        })

        return {
            feed,
            activity,
            loadingActivity,
            navigateToActivity,
        }
    }
}
</script>

<style scoped>

</style>
